import * as React from 'react';
import { TableWidgetProps, TableWidget, TableColumn, TableRow } from './TableWidget';
import { ControlledSearchWidget } from '../search/ControlledSearchWidget';
import { SearchEventMatcher, createSearchEventMatcher } from '../../search/SearchEngineInterface';
import { styled } from '../../theme';
import { Btn, ButtonWidget } from '../buttons/ButtonWidget';
import * as _ from 'lodash';
import { EmptyItemsWidget } from '../EmptyItemsWidget';

export interface SearchableTableColumn<T extends TableRow = TableRow> extends TableColumn<T> {
  accessorSearch?: (cell: any, row: any) => string | null;
}

export interface SearchableTableWidgetProps<T extends TableRow = TableRow> extends TableWidgetProps<T> {
  columns: SearchableTableColumn<T>[];
  controlButtons?: null | (Btn | JSX.Element)[];
  emptyLabel?: string;
  onSearch?: (searchValue: string | null) => any;
}

export interface SearchableTableWidgetState {
  matcher: SearchEventMatcher | null;
}

export class SearchableTableWidget<T extends TableRow = TableRow> extends React.Component<
  SearchableTableWidgetProps<T>,
  SearchableTableWidgetState
> {
  constructor(props: SearchableTableWidgetProps<T>) {
    super(props);
    this.state = {
      matcher: null
    };
  }

  getRows = () => {
    if (!this.state.matcher) {
      return this.props.rows;
    }
    return this.props.rows.filter((row) => {
      for (let col of this.props.columns) {
        if (!col.accessorSearch) {
          continue;
        }
        const searchValue = col.accessorSearch(row.cells[col.key], row);
        if (!!searchValue && !!this.state.matcher?.(searchValue)) {
          return true;
        }
      }
      return false;
    });
  };

  getEmptyMessage = () => {
    return <EmptyItemsWidget label={this.props.emptyLabel || 'No items to display'} />;
  };

  filter = _.debounce((search) => {
    this.setState(
      {
        matcher: search ? createSearchEventMatcher(search) : null
      },
      () => {
        this.props.onSearch?.(search);
      }
    );
  }, 100);

  render() {
    const rows = this.getRows();
    return (
      <S.Container>
        <S.Controls>
          <ControlledSearchWidget searchChanged={this.filter} />
          {this.props.controlButtons?.map((button, idx) => {
            if (React.isValidElement(button)) {
              return button;
            }
            return <S.Button key={`control-button-${idx}`} {...(button as Btn)} />;
          })}
        </S.Controls>
        <TableWidget {...this.props} rows={rows} />
        {rows.length === 0 && !this.props.loading ? this.getEmptyMessage() : null}
      </S.Container>
    );
  }
}

namespace S {
  export const Container = styled.div``;

  export const Controls = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-bottom: 15px;
  `;

  export const Button = styled(ButtonWidget)`
    margin-left: 8px;
  `;
}
